import * as React from 'react';
import { isNil, isArray } from 'lodash/fp';
import { isEqual, isFunction, isString } from 'lodash';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  PopperProps,
  Popper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete, AutocompleteClassKey } from '@material-ui/lab';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { classes } from 'typestyle';
import { Check, Close, KeyboardArrowDown } from '@material-ui/icons';

import styles, {
  baseAutocompleteStyles,
  customPopperStyle,
  generalPopper,
  generalPopperHeight,
  actionButtons,
  getChipStyles,
} from './InputSuggest.styles';
import VirtualizedListbox, {
  type VirtualizedListboxProps,
} from 'src/common-ui/components/VirtualizedListbox/VirtualizedListbox';
import { AutocompleteCloseReason } from '@material-ui/lab/useAutocomplete/useAutocomplete';

export const selectAll: Suggestion = { label: 'Select All', value: '$selectAll' };
export const deselectAll: Suggestion = { label: 'Deselect All', value: '$selectAll' };

/** Checks for null, undefined, or empty string. isNil doesnt catch empty string & isEmpty is true for numbers */
function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

const ErrorTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: 'rgba(247, 92, 105, 0.97)',
    color: '#FFF',
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}))(Tooltip);

export const ErrorIcon = (props: Suggestion) => {
  return props.icon ? (
    <ErrorTooltip title="Conflict: Current value not found in valid values.">
      <i className={classes(styles.errorIcon, props.icon)} />
    </ErrorTooltip>
  ) : null;
};

interface ListboxVirtualizedProps extends VirtualizedListboxProps {}

export const ListboxVirtualized = React.forwardRef<HTMLUListElement, ListboxVirtualizedProps>(
  function ListboxVirtualized(listboxProps, ref) {
    const { children, selectedCount = 0, totalCount = 0, onToggleAll, ...rest } = listboxProps;

    return !rest.isMultiSelect ? (
      <VirtualizedListbox style={{ padding: 0 }} ref={ref} {...rest}>
        {children}
      </VirtualizedListbox>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 'inherit' }}>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: '1px 3px',
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Selected: {selectedCount} / {totalCount}
          </Typography>
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Checkbox
                name="selectAll"
                size="small"
                style={{ padding: '0 5px' }}
                checked={selectedCount === totalCount}
                onMouseDown={(event) => {
                  // This is very important for multiselect headitors that implement InputSuggest (ValidValuesHeaditor).
                  // Doing this prevents the checkbox from stealing focus from the active element the TextField's input.
                  // Without it, clicking the checkbox while in a multiselect headitor would cause the TextField's blur handler to be triggered.
                  // There should be no affect to any behavior of the cell editors, only when used as a headitor.
                  event.preventDefault();
                }}
                onChange={(_evt, checked) => {
                  if (onToggleAll) {
                    if (checked) {
                      onToggleAll([selectAll]);
                    } else {
                      onToggleAll([deselectAll]);
                    }
                  }
                }}
              />
            }
            label={
              <Typography variant="caption" color="textSecondary">
                Select All
              </Typography>
            }
            labelPlacement="start"
          />
        </Box>

        <Divider variant="fullWidth" />

        <VirtualizedListbox style={{ padding: 0 }} ref={ref} {...rest}>
          {children}
        </VirtualizedListbox>
      </div>
    );
  },
);

interface PopperWidthOverrideProps extends PopperProps {
  popperWidth?: number;
}

const PopperWidthOverride = function ({ popperWidth, ...restProps }: PopperWidthOverrideProps) {
  const { anchorEl, style } = restProps;
  let newWidth = popperWidth ?? style?.width;

  if (!popperWidth && anchorEl != null) {
    const el = isFunction(anchorEl) ? anchorEl() : anchorEl;
    newWidth = el.clientWidth;
  }

  return <Popper {...restProps} placement="bottom-start" style={{ ...style, width: newWidth, minWidth: newWidth }} />;
};

export interface CustomAutocompleteClasses {
  /** Overrides passed directly to the Autocomplete component's `classes` property  */
  autocomplete?: Partial<Record<AutocompleteClassKey, string>>;
}

export interface MultiselectInputSuggestProps {
  multiSelect?: boolean;
  /** When true, render the selected values as chips in the input */
  showChips?: boolean;
}

interface InputSuggestProps extends MultiselectInputSuggestProps {
  classes?: CustomAutocompleteClasses;
  validValues: Suggestion[];
  selected: Suggestion[] | Suggestion | string | undefined;
  valid?: boolean;
  placeholder?: string;
  onSelect?: (sugg: Suggestion) => void;
  onMultiSelect?: (suggs: Suggestion[]) => void;
  dataQa?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  defaultOpenOnFocus?: boolean;
  /** **For lazy loading** */
  open?: boolean;
  onOpen?: () => void;
  onClose?: (event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => void;
  allowPortal?: boolean;
  autofocus?: boolean;
  /** ability to specify a wider width for the dropdown */
  popperWidth?: number;
  onCancel?: () => void;
  onSubmit?: () => void;
}

export interface Suggestion {
  label: string;
  value: string | number | object;
  icon?: string;
}
export default class InputSuggest extends React.Component<InputSuggestProps> {
  static defaultProps = {
    multiSelect: false,
  };

  private inputElement: HTMLInputElement | null = null;
  private BoxRef = React.createRef<HTMLDivElement>();
  lastHighlightedIndex: number = -1;
  lastHighlightedOption: Suggestion | null = null;
  componentDidUpdate(prevProps: InputSuggestProps) {
    // when dropdown just opened AND it’s not loading yet…
    const justOpened = !prevProps.open && this.props.open;
    const loadFinished = prevProps.isLoading && !this.props.isLoading && this.props.open;

    if (justOpened || loadFinished) {
      // this is required for selection of text in style edit section, see INT-5048
      // give React one tick to actually mount the <input>
      this.inputFocus();
    }
  }
  componentDidMount() {
    if (this.props.autofocus) {
      this.inputFocus();
    }
  }
  inputFocus() {
    setTimeout(() => {
      if (this.inputElement) {
        this.inputElement.focus();
        this.inputElement.select();
      }
    }, 0);
  }
  shouldComponentUpdate(nextProps: Readonly<InputSuggestProps>): boolean {
    return !isEqual(nextProps, this.props);
  }

  getSelection() {
    const { selected } = this.props;
    if (isNil(selected)) return [];
    if (isString(selected)) {
      return [
        {
          label: selected,
          value: selected,
          icon: 'fal fa-exclamation-triangle',
        },
      ];
    }
    if (!isArray(selected) && selected.value === '') {
      return [];
    }
    return isArray(selected) ? selected : [selected];
  }

  handleSingleSelect = (selection: Suggestion | null) => {
    const { onSelect } = this.props;
    if (selection && onSelect) {
      onSelect(selection);
    }
  };

  handleMultiSelect = (selections: Suggestion[]) => {
    const { onMultiSelect } = this.props;
    if (onMultiSelect) {
      onMultiSelect(selections);
    }
  };

  sortValidValues = () => {
    const { validValues } = this.props;
    const selectedValues = this.getSelection().map((selection) => selection.value);

    const selectedItems = validValues.filter((vv) => selectedValues.includes(vv.value));
    const unselectedItems = validValues.filter((vv) => !selectedValues.includes(vv.value));

    return [...selectedItems, ...unselectedItems];
  };

  render() {
    const {
      validValues,
      multiSelect,
      placeholder,
      dataQa,
      disabled,
      isLoading,
      onBlur,
      onFocus,
      open,
      onOpen,
      onClose,
      defaultOpenOnFocus,
      allowPortal = false,
      onCancel,
      onSubmit,
      classes: overrides,
      showChips = false,
    } = this.props;

    const selected = this.getSelection();

    // Only auto-highlight the first option when it's not multiSelect AND the selected option has empty value
    const shouldAutoHighlight = !multiSelect && (selected.length === 0 || selected[0]?.value === '');

    // Find the index of the selected option in the validValues array
    const selectedIndex =
      !multiSelect && selected.length > 0 && selected[0]?.value !== ''
        ? validValues.findIndex((option) => option.value === selected[0].value)
        : -1;
    const highlightedIndex = multiSelect ? this.lastHighlightedIndex : shouldAutoHighlight ? 0 : selectedIndex;
    const options = validValues;
    const showActionButtons = multiSelect && (!!onCancel || !!onSubmit);

    const base = baseAutocompleteStyles;
    // apply base and override styles cleanly if both exist
    const mergedClasses = {
      ...base,
      root: classes(base.root, overrides?.autocomplete?.root),
      inputRoot: classes(base.inputRoot, overrides?.autocomplete?.inputRoot),
      input: classes(base.input, overrides?.autocomplete?.input),
      // `ag-custom-component-popup` allows custom popups from editors (dropdown options) to properly notify ag-grid when to stop editing
      popper: classes(
        generalPopper,
        allowPortal ? 'ag-custom-component-popup' : customPopperStyle,
        overrides?.autocomplete?.popper,
      ),
    };

    return (
      <FormGroup className={styles.container} data-qa="form-group-container">
        <div className={classes('select-container', disabled ? 'disabled' : '')} ref={this.BoxRef}>
          <Box style={{ backgroundColor: 'white' }} className="input-suggest-container" data-qa={dataQa}>
            <Autocomplete
              forcePopupIcon
              selectOnFocus
              disablePortal={!allowPortal}
              disableCloseOnSelect={multiSelect || showActionButtons}
              disableClearable={multiSelect}
              openOnFocus={multiSelect || defaultOpenOnFocus}
              multiple={multiSelect}
              disabled={disabled}
              loading={isLoading}
              options={options}
              open={open}
              PopperComponent={(props: PopperProps) => {
                return (
                  <PopperWidthOverride
                    {...props}
                    data-qa="input-suggest-popper"
                    anchorEl={this.BoxRef.current}
                    popperWidth={this.props.popperWidth}
                  />
                );
              }}
              PaperComponent={(props) => (
                <Paper {...props}>
                  {props.children}
                  {showActionButtons && (
                    <div className={actionButtons} onMouseDown={(event) => event.preventDefault()}>
                      <IconButton
                        aria-label="Cancel selection"
                        data-qa="cancel-selection"
                        onClick={onCancel}
                        size="small"
                      >
                        <Close />
                      </IconButton>
                      <IconButton
                        aria-label="Submit selection"
                        data-qa="submit-selection"
                        onClick={onSubmit}
                        size="small"
                      >
                        <Check />
                      </IconButton>
                    </div>
                  )}
                </Paper>
              )}
              onOpen={onOpen}
              onClose={onClose}
              popupIcon={<KeyboardArrowDown />}
              classes={mergedClasses}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              value={multiSelect ? selected : selected[0] || null}
              onChange={(_evt, newValue) => {
                if (!multiSelect) {
                  // Map MUI "clear" (null) to an null Suggestion so it propagates properly upwards
                  const next: Suggestion = (newValue as Suggestion) ?? { label: null, value: null };
                  this.handleSingleSelect(next);
                } else {
                  this.handleMultiSelect((newValue as Suggestion[]) || []);
                }
              }}
              loadingText="Loading..."
              renderOption={(option) => (
                <div
                  data-qa="option-value"
                  style={{
                    ...(isEmptyValue(option.value) ? { color: '#9e9e9e', fontStyle: 'italic' } : {}),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {isEmptyValue(option.value) ? '(empty)' : option.label || option.value}
                </div>
              )}
              // conditionally render chips, otherwise selection appears in the options list
              renderTags={
                showChips
                  ? (value: Suggestion[], getTagProps) => (
                      <div
                        style={multiSelect ? { overflowY: 'auto', marginBottom: '3px' } : undefined}
                        data-qa="chips-wrapper"
                      >
                        {value.map((option, index) => (
                          <Chip
                            deleteIcon={<Close aria-label="Delete option" />}
                            key={index}
                            label={option.label}
                            {...getTagProps({ index })}
                            size="small"
                          />
                        ))}
                      </div>
                    )
                  : () => null
              }
              // Track MUI's internal highlighted option.
              // Without this, Autocomplete may reset the highlight to the first option
              // after selecting, which triggers a scroll-to-top in the virtualized list.
              onHighlightChange={(_e, option) => {
                this.lastHighlightedOption = (option as Suggestion) ?? null;
                this.lastHighlightedIndex = option
                  ? this.props.validValues.findIndex((v) => v.value === (option as Suggestion).value)
                  : -1;
              }}
              ListboxComponent={ListboxVirtualized as unknown as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
              ListboxProps={{
                maxListHeight: generalPopperHeight,
                autoHighlight: shouldAutoHighlight,
                highlightedIndex: highlightedIndex,
                isMultiSelect: !!multiSelect,
                selectedCount: selected.length,
                totalCount: options.length,
                onToggleAll: this.handleMultiSelect,
                key: multiSelect ? 'multiselect-list' : 'singleselect-list',
              }}
              renderInput={(params) => {
                const endAdornmentChildren = React.Children.toArray(
                  React.isValidElement(params.InputProps.endAdornment)
                    ? params.InputProps.endAdornment.props.children
                    : [],
                );

                return (
                  <TextField
                    {...params}
                    placeholder={isLoading ? '' : (placeholder ?? 'Search...')}
                    variant="outlined"
                    style={{
                      backgroundColor: disabled ? 'rgb(242, 242, 242)' : undefined,
                    }}
                    onBlur={onBlur}
                    autoFocus={true}
                    onFocus={(e) => {
                      // TODO: verify any of this code is still needed now that autofocus on the TextField works as intended
                      // this is required for selection of text in ag-grid use, see INT-5048
                      e.target.select();
                      if (onFocus) {
                        onFocus();
                      }
                      const el = this.inputElement;
                      if (el) {
                        el.scrollLeft = 0;
                        const length = el.value.length;
                        el.setSelectionRange(0, length);
                      }
                    }}
                    value={multiSelect ? selected : selected[0] || null}
                    data-qa="selected-option"
                    inputRef={(node) => {
                      // This allows for the input to properly focus onClick,
                      // it's assigning the inputRef we instantiate above
                      // without it - focus & clickaway do not work
                      this.inputElement = node;
                      if (params.InputProps.ref) {
                        (params.InputProps.ref as React.RefCallback<HTMLInputElement>)(node);
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>{multiSelect ? (params.InputProps.startAdornment ?? null) : <ErrorIcon {...selected[0]} />}</>
                      ),
                      endAdornment: (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            pr: 1,
                          }}
                        >
                          {endAdornmentChildren.length > 0 &&
                            endAdornmentChildren.map((child, index) => (
                              <React.Fragment key={index}>
                                {((endAdornmentChildren.length === 1 && index === 0) ||
                                  (endAdornmentChildren.length > 1 && index > 0)) && (
                                  <Box
                                    width="1px"
                                    height={multiSelect ? '85%' : '50%'}
                                    mx={0.5}
                                    bgcolor="rgba(98, 95, 95, 0.2)"
                                    alignSelf="center"
                                  />
                                )}
                                {child}
                              </React.Fragment>
                            ))}
                        </Box>
                      ),
                    }}
                  />
                );
              }}
            />
          </Box>
        </div>
      </FormGroup>
    );
  }
}
